import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IODataResponse } from 'src/app/core/models/odata.models';
import { ApiService } from 'src/app/core/services/api.service';
import { HttpService } from 'src/app/core/services/http.service';
import { IDocument } from 'src/app/features/customers/models/customer-document.models';
import { normalizeEntityId } from 'src/app/shared/helpers/entity-id.helper';
import { DeliveryTerm } from '../../shipping-methods/models/shipping-method.models';
import {
    ICustomerOrderDispositionSuggestionRequestDto,
    ICustomerOrderDispositionSuggestionResponseDto,
    ICustomerOrderPositionDispositionSuggestionDto,
    ICustomerOrderPositionDispositionSuggestionRequestDto,
} from '../models/disposition.models';
import {
    CUSTOMER_ORDER_POSITIONS_LIST_QUERY,
    IBulkUpdateConfig,
    IBulkUpdateCustomerOrderPositionsRequestDto,
    IBulkUpdateCustomerOrderRequestDto,
    ICustomerOrder,
    ICustomerOrderPositionRequestDto,
    ICustomerOrderPositionResponseDto,
    ICustomerOrderResponseDto,
    IOrderPatchRequest,
    IPatchCustomerOrderPositionRequestDto,
} from '../models/order.models';

@Injectable({ providedIn: 'root' })
export class CustomerOrdersService {
    private readonly httpService = inject(HttpService);
    private readonly apiService = inject(ApiService);

    /**
     * Loads a single customer order by identifier.
     */
    getCustomerOrderById(orderId: string): Observable<ICustomerOrderResponseDto> {
        return this.httpService.get$<ICustomerOrderResponseDto>(this.apiService.pathUrl.customerOrderById(orderId));
    }

    /**
     * Loads multiple customer orders by identifier (normalized and deduped).
     */
    getCustomerOrdersById(ids: string[]): Observable<ICustomerOrderResponseDto[]> {
        const uniqueIds = [...new Set(ids.map(id => normalizeEntityId(id)).filter(Boolean))];
        if (uniqueIds.length === 0) {
            return of([]);
        }
        return forkJoin(uniqueIds.map(id => this.getCustomerOrderById(id)));
    }

    /**
     * Creates a new customer order from a partial payload.
     */
    createCustomerOrder(payload: Partial<ICustomerOrder>): Observable<ICustomerOrder> {
        return this.httpService.post$<ICustomerOrder>(this.apiService.pathUrl.customerOrders(), payload, new HttpParams());
    }

    /**
     * Updates an existing customer order.
     */
    updateCustomerOrder(orderId: string, payload: Partial<ICustomerOrder>): Observable<ICustomerOrderResponseDto> {
        const data = { ...payload };
        delete data.version;
        delete data.id;
        const params = this.apiService.params({ version: payload.version });
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerOrderById(orderId)}?${paramsString}`
            : this.apiService.pathUrl.customerOrderById(orderId);
        return this.httpService.put$<ICustomerOrderResponseDto>(url, data);
    }

    /**
     * Deletes a customer order by identifier.
     */
    deleteCustomerOrder(orderId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerOrderById(orderId));
    }

    /**
     * Loads a single customer order by identifier.
     */
    getAllCustomerOrderPositions(orderId: string): Observable<ICustomerOrderPositionResponseDto[]> {
        return this.httpService
            .get$<IODataResponse<ICustomerOrderPositionResponseDto>>(
                this.apiService.pathUrl.customerOrderPositionsById(orderId, CUSTOMER_ORDER_POSITIONS_LIST_QUERY)
            )
            .pipe(map(response => response.value));
    }

    getCustomerOrderPositionById(orderId: string, posId: string): Observable<ICustomerOrderPositionResponseDto> {
        return this.httpService.get$<ICustomerOrderPositionResponseDto>(this.apiService.pathUrl.customerOrderPositionById(orderId, posId));
    }

    getCustomerOrderSources(): Observable<string[]> {
        return this.httpService.get$<string[]>(this.apiService.pathUrl.customerOrderSources());
    }

    getCustomerOrderSalesRep(): Observable<string[]> {
        return this.httpService.get$<string[]>(this.apiService.pathUrl.customerOrderSalesReps());
    }

    createCustomerOrderPosition(
        orderId: string,
        payload: Partial<ICustomerOrderPositionRequestDto>
    ): Observable<ICustomerOrderPositionResponseDto> {
        return this.httpService.post$<ICustomerOrderPositionResponseDto>(
            this.apiService.pathUrl.customerOrderPositionsById(orderId),
            payload,
            new HttpParams()
        );
    }

    updateCustomerOrderPosition(
        orderId: string,
        positionId: string,
        payload: Partial<ICustomerOrderPositionRequestDto>,
        version?: string | null
    ): Observable<ICustomerOrderPositionResponseDto> {
        const params = this.apiService.params({ version });
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerOrderPositionById(orderId, positionId)}?${paramsString}`
            : this.apiService.pathUrl.customerOrderPositionById(orderId, positionId);

        return this.httpService.put$<ICustomerOrderPositionResponseDto>(url, payload);
    }

    patchCustomerOrderPosition(
        orderId: string,
        positionId: string,
        payload: IPatchCustomerOrderPositionRequestDto,
        version?: string | null
    ): Observable<ICustomerOrderPositionResponseDto> {
        const params = this.apiService.params({ version });
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerOrderPositionById(orderId, positionId)}?${paramsString}`
            : this.apiService.pathUrl.customerOrderPositionById(orderId, positionId);

        return this.httpService.patch$<ICustomerOrderPositionResponseDto>(url, payload);
    }

    /**
     * Deletes a customer order by identifier.
     */
    deleteCustomerOrderPosition(orderId: string, positionId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerOrderPositionById(orderId, positionId));
    }

    bulkSetPositionsReadyForDelivery(orderId: string, positions: IBulkUpdateCustomerOrderRequestDto[]): Observable<void> {
        const payload: IBulkUpdateCustomerOrderPositionsRequestDto = {
            positions,
            isReadyForDelivery: true,
        };
        const url = this.apiService.pathUrl.customerOrderUpdatePositionDate(orderId);
        return this.httpService.patch$(url, payload);
    }

    getCustomerOrderDocuments(orderId: string): Observable<IDocument[]> {
        return this.httpService.get$<IODataResponse<IDocument>>(this.apiService.pathUrl.documents('customer-orders', orderId)).pipe(
            map(response => response.value),
            catchError((error: unknown) => {
                if (typeof error === 'object' && error !== null && 'status' in error && (error as { status?: number }).status === 404) {
                    return of([] as IDocument[]);
                }
                throw error;
            })
        );
    }

    getCustomerOrderDocumentById(orderId: string, documentId: string): Observable<IDocument> {
        return this.httpService.get$<IDocument>(this.apiService.pathUrl.documentById('customer-orders', orderId, documentId));
    }

    createCustomerOrderDocument(orderId: string, file: File): Observable<IDocument> {
        const formData = new FormData();
        formData.append('file', file);
        return this.httpService.post$<IDocument>(this.apiService.pathUrl.documents('customer-orders', orderId), formData, new HttpParams());
    }

    getCustomerOrderDocumentContent(orderId: string, documentId: string): Observable<Blob> {
        return this.httpService.getBlob$(this.apiService.pathUrl.documentContent('customer-orders', orderId, documentId));
    }

    updateCustomerOrderDocumentContent(orderId: string, documentId: string, file: File): Observable<void> {
        const formData = new FormData();
        formData.append('file', file);
        return this.httpService.put$<void>(this.apiService.pathUrl.documentContent('customer-orders', orderId, documentId), formData);
    }

    deleteCustomerOrderDocument(orderId: string, documentId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.documentById('customer-orders', orderId, documentId));
    }

    downloadCustomerOrderDocument(orderId: string, documentId: string, filename: string | null): Observable<void> {
        return this.getCustomerOrderDocumentContent(orderId, documentId).pipe(
            map(blob => {
                const url = globalThis.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename || 'document';
                document.body.appendChild(link);
                link.click();
                link.remove();
                globalThis.URL.revokeObjectURL(url);
            })
        );
    }

    patchOrder(orderId: string, payload: IOrderPatchRequest, version: string): Observable<ICustomerOrderResponseDto> {
        const params = this.apiService.params({ version });
        const paramsString = params.toString();
        const url = paramsString
            ? `${this.apiService.pathUrl.customerOrderById(orderId)}?${paramsString}`
            : this.apiService.pathUrl.customerOrderById(orderId);
        return this.httpService.patch$<ICustomerOrderResponseDto>(url, payload);
    }

    bulkUpdateDeliveryDates(config: IBulkUpdateConfig, orders: IBulkUpdateCustomerOrderRequestDto[]) {
        const payload = {
            customerOrders: orders,
            targetDeliveryDate: config.deliveryDate,
            setDeliveryDateForPositions: config.applyToPosition,
        };
        const url = this.apiService.pathUrl.customerOrders();
        return this.httpService.patch$(url, payload);
    }

    bulkUpdatePositionDeliveryDates(config: IBulkUpdateConfig, positions: IBulkUpdateCustomerOrderRequestDto[], orderId: string) {
        const payload = {
            positions: positions,
            targetDeliveryDate: config.deliveryDate,
        };
        const url = this.apiService.pathUrl.customerOrderUpdatePositionDate(orderId);
        return this.httpService.patch$(url, payload);
    }

    getOrdersFromCustomer(customerId: string): Observable<IODataResponse<ICustomerOrder>> {
        const url = this.apiService.pathUrl.customerOrders(`?$filter=customerId eq ${customerId}`);
        return this.httpService.get$<IODataResponse<ICustomerOrder>>(url);
    }

    /**
     * Resolves a customer order by its human-readable order number (e.g. for external deep links
     * that only know the order number, not the internal id).
     */
    getCustomerOrderByNumber(customerOrderNumber: string): Observable<ICustomerOrder | null> {
        const safeNumber = customerOrderNumber.trim().replaceAll("'", "''");
        const url = this.apiService.pathUrl.customerOrders(`?$filter=customerOrderNumber eq '${safeNumber}'&$top=1`);
        return this.httpService.get$<IODataResponse<ICustomerOrder>>(url).pipe(map(response => response.value[0] ?? null));
    }

    /**
     * Resolves a customer order by its external (foreign-system) reference number, for deep links
     * from applications that only know that number, not our order number or internal id.
     */
    getCustomerOrderByExternalReferenceNo(externalReferenceNo: string): Observable<ICustomerOrder | null> {
        const safeNumber = externalReferenceNo.trim().replaceAll("'", "''");
        const url = this.apiService.pathUrl.customerOrders(`?$filter=externalReferenceNo eq '${safeNumber}'&$top=1`);
        return this.httpService.get$<IODataResponse<ICustomerOrder>>(url).pipe(map(response => response.value[0] ?? null));
    }

    getOrdersForConsolidatedDelivery(
        customerId: string,
        deliveryTerms: DeliveryTerm,
        shippingMethodId: string,
        differingDeliveryAddressId: string
    ): Observable<IODataResponse<ICustomerOrder[]>> {
        const filter = `?$filter=customerId eq ${customerId} and deliveryTerms eq '${deliveryTerms}' and shippingMethodId eq ${shippingMethodId} and differingDeliveryAddress/clientAddressId eq ${differingDeliveryAddressId}`;
        const expand = `&$expand=positions,differingDeliveryAddress`;
        const url = this.apiService.pathUrl.customerOrders(`${filter}${expand}`);
        return this.httpService.get$<IODataResponse<ICustomerOrder[]>>(url);
    }

    getCustomerOrderDispositionSuggestion(orderId: string): Observable<ICustomerOrderDispositionSuggestionResponseDto> {
        return this.httpService.get$<ICustomerOrderDispositionSuggestionResponseDto>(
            this.apiService.pathUrl.customerOrderDispositionSuggestion(orderId)
        );
    }

    executeCustomerOrderDisposition(orderId: string, payload: ICustomerOrderDispositionSuggestionRequestDto): Observable<void> {
        return this.httpService.post$<void>(this.apiService.pathUrl.customerOrderDisposition(orderId), payload, new HttpParams());
    }

    getCustomerOrderPositionDispositionSuggestion(
        orderId: string,
        positionId: string
    ): Observable<ICustomerOrderPositionDispositionSuggestionDto> {
        return this.httpService.get$<ICustomerOrderPositionDispositionSuggestionDto>(
            this.apiService.pathUrl.customerOrderPositionDispositionSuggestion(orderId, positionId)
        );
    }

    executeCustomerOrderPositionDisposition(
        orderId: string,
        positionId: string,
        payload: ICustomerOrderPositionDispositionSuggestionRequestDto
    ): Observable<void> {
        return this.httpService.post$<void>(
            this.apiService.pathUrl.customerOrderPositionDisposition(orderId, positionId),
            payload,
            new HttpParams()
        );
    }
}
